import { useState } from 'react';
import { FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { submitLeadMagnet } from '@/lib/api';

export default function LeadMagnet() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please share your name and email.');
      return;
    }
    setLoading(true);
    try {
      await submitLeadMagnet({ name, email, source: 'legacy-readiness-checklist' });
      setDone(true);
      toast.success('Your checklist is on its way.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="lead-magnet-section"
      className="relative bg-[#F7F4EE] py-20 md:py-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="relative bg-[#0E2238] p-10 md:p-16 border border-[#B68A35]/30 overflow-hidden">
          {/* Corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-l border-t border-[#D6B36A]" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-r border-b border-[#D6B36A]" />
          {/* Glow */}
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(214,179,106,0.5) 0%, transparent 70%)' }}
          />

          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-3 text-[#D6B36A] text-xs tracking-[0.28em] uppercase font-medium mb-5">
                <FileText className="w-4 h-4" />
                <span>Complimentary Guide</span>
              </div>
              <h3 className="font-serif text-white text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mb-5">
                The Legacy Readiness<br />
                <span className="italic gold-text">Checklist</span>.
              </h3>
              <p className="text-white/70 text-base leading-relaxed font-light">
                A simple guide to help you identify the conversations, protections, and planning
                steps every family should consider.
              </p>
            </div>

            <div>
              {done ? (
                <div
                  data-testid="lead-magnet-success"
                  className="flex items-center gap-4 p-6 border border-[#D6B36A]/40 bg-[#070B12]/40"
                >
                  <div className="w-12 h-12 rounded-full bg-[#B68A35]/20 flex items-center justify-center check-in">
                    <Check className="w-6 h-6 text-[#D6B36A]" strokeWidth={2} />
                  </div>
                  <div className="fade-up" style={{ animationDelay: '200ms' }}>
                    <p className="text-white font-serif text-xl">Thank you.</p>
                    <p className="text-white/60 text-sm font-light mt-1">Anthony will send the checklist to your inbox shortly.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="lead-magnet-form">
                  <div>
                    <label className="text-white/50 text-[0.7rem] tracking-[0.24em] uppercase">Full Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="lux-input-dark"
                      data-testid="lead-magnet-name"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-[0.7rem] tracking-[0.24em] uppercase">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="lux-input-dark"
                      data-testid="lead-magnet-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-magnet-submit"
                    className="btn-gold w-full disabled:opacity-60"
                  >
                    {loading ? 'Sending…' : 'Request the Checklist'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
